import { useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { useAppToast } from '../components/feedback/useAppToast'
import useAuth from './useAuth'
import { notificationsService } from '../services/notifications.service'
import { tokenStore } from '../services/tokenStore'
import type { ApiNotification, NotificationsListData } from '../types/api'
import type { Permission } from '../types/permissions'
import { Action, Resource } from '../types/permissions'
import { playNotificationSound } from '../utils/notificationSound'

/** Any notifications scope from /me enables SSE toast + sound (aligns with `notifications` key in profile). */
const NOTIFICATION_TOAST_PERMISSIONS: Permission[] = [
  `${Resource.Notifications}:${Action.View}`,
  `${Resource.Notifications}:${Action.Create}`,
  `${Resource.Notifications}:${Action.Update}`,
  `${Resource.Notifications}:${Action.Delete}`,
]

interface SseNotificationEvent {
  id: string
  type: string
  isRead: boolean
  createdAt: string
  message?: string
  action?: { title?: string; verb?: string; description?: string }
  actor?: { id?: string; name?: string; email?: string; role?: string; type?: string }
  target?: { id?: string; name?: string; type?: string; role?: string; cycle?: string; amount?: string }
  deepLink?: string
}

interface UseNotificationSSEOptions {
  isOnNotificationsPage: boolean
}

const toNotification = (data: SseNotificationEvent): ApiNotification => ({
  id: data.id,
  type: data.type,
  isRead: data.isRead,
  createdAt: data.createdAt,
  ...(data.message && { message: data.message }),
  ...(data.action && { action: data.action }),
  ...(data.actor && { actor: data.actor }),
  ...(data.target && { target: data.target }),
  ...(data.deepLink && { deepLink: data.deepLink }),
})

const prependToCache = (queryClient: QueryClient, notification: ApiNotification): void => {
  // Update all notification caches — both the sidebar (base key) and page caches
  queryClient.setQueriesData<NotificationsListData>({ queryKey: notificationsService.queryKeys.list }, (old) => {
    if (!old) return old

    const existingIndex = old.notifications.findIndex((n) => n.id === notification.id)
    if (existingIndex !== -1) {
      // Merge SSE data (especially the richer `message`) into the existing entry
      const updated = [...old.notifications]
      updated[existingIndex] = { ...updated[existingIndex], ...notification }
      return { ...old, notifications: updated }
    }

    // Keep the list within the original limit to avoid growing indefinitely
    const limit = old.meta?.limit ?? 10
    const trimmed = [notification, ...old.notifications].slice(0, limit)

    return {
      ...old,
      notifications: trimmed,
      unreadCount: old.unreadCount + 1,
      meta: old.meta ? { ...old.meta, total: old.meta.total + 1 } : old.meta,
    }
  })
}

const handleSseMessage = (
  event: MessageEvent,
  queryClient: QueryClient,
  isOnNotificationsPage: boolean,
  showNotificationToast: (title: string, body: string) => void,
  currentUserId: string | undefined,
  canShowNotificationToast: boolean,
): void => {
  try {
    const data = JSON.parse(event.data as string) as SseNotificationEvent
    const notification = toNotification(data)

    // Always add to cache so it appears on the notifications page
    prependToCache(queryClient, notification)

    // Skip toast & sound for notifications triggered by the current user or already read
    const isSelfAction = currentUserId != null && data.actor?.id === currentUserId

    if (canShowNotificationToast && !isOnNotificationsPage && !isSelfAction && data.isRead === false) {
      playNotificationSound()
      const title = data.action?.title ?? 'New Notification'
      const body = data.message?.trim() ?? ''
      showNotificationToast(title, body)
    }
  } catch {
    // Ignore malformed SSE events
  }
}

const useNotificationSSE = ({ isOnNotificationsPage }: UseNotificationSSEOptions): void => {
  const queryClient = useQueryClient()
  const { showNotificationToast } = useAppToast()
  const { user, hasPermission } = useAuth()
  const currentUserId = user?.id

  const canShowNotificationToast = useMemo(
    () => NOTIFICATION_TOAST_PERMISSIONS.some((p) => hasPermission(p)),
    [hasPermission],
  )

  useEffect(() => {
    const token = tokenStore.get()

    if (!token) {
      return () => undefined
    }

    const baseUrl = process.env.REACT_APP_BASE_URL ?? ''
    const url = `${baseUrl}${notificationsService.endpoints.stream}?token=${token}`
    const es = new EventSource(url)

    es.onmessage = (event: MessageEvent) =>
      handleSseMessage(
        event,
        queryClient,
        isOnNotificationsPage,
        showNotificationToast,
        currentUserId,
        canShowNotificationToast,
      )
    es.onerror = () => es.close()

    return () => {
      es.close()
    }
  }, [queryClient, isOnNotificationsPage, showNotificationToast, currentUserId, canShowNotificationToast])
}

export default useNotificationSSE
